import type { DeclaredUnit } from "@/stores/project/types/enum";

// The declared-unit enum API (api/material/declaredunitenum) returns spelled-out
// names in DisplayName ("Square meters", "Cubic meters", "Kilograms"). The cards
// and datasheet want the compact symbol ("m²", "m³", "kg") instead. The Enum code
// is the stable key to map from; unknown codes fall back to DisplayName so nothing
// regresses.
const DECLARED_UNIT_SYMBOLS: Partial<Record<DeclaredUnit["Enum"], string>> = {
  G: "g",
  KG: "kg",
  T: "t",
  M: "m",
  M2: "m²",
  M3: "m³",
  PCS: "pcs",
  MJ: "MJ",
  KGKM: "kg·km",
};

/**
 * Resolve a declared unit to its display symbol.
 * Returns the mapped symbol (e.g. "m²") when the Enum code is known, otherwise
 * falls back to the backend DisplayName, and finally to an empty string.
 */
export function declaredUnitSymbol(unit: Pick<DeclaredUnit, "Enum" | "DisplayName"> | undefined | null): string {
  if (!unit) return "";
  return DECLARED_UNIT_SYMBOLS[unit.Enum] ?? unit.DisplayName ?? "";
}

/**
 * Resolve a raw declared-unit code string (e.g. "M3") to its display symbol ("m³").
 * Strings that aren't known codes — already-formatted symbols and Hasura unit
 * labels like "m³" — pass through unchanged.
 */
export function declaredUnitCodeSymbol(code: string): string {
  const symbols: Record<string, string | undefined> = DECLARED_UNIT_SYMBOLS;
  return symbols[code] ?? code;
}
